import { emptySettings } from "../../landingEditor/config";
import {
  DEFAULT_SEGMENT_VISUAL_STYLE,
  SEGMENT_LABELS,
} from "../segmentVisualStyle";

const SEGMENT_FIELD_TYPES = Object.freeze({
  TEXT: "text",
  TEXTAREA: "textarea",
  BOOLEAN: "boolean",
  URL: "url",
  EMAIL: "email",
  COLOR: "color",
  IMAGE: "image",
  SELECT: "select",
  NUMBER: "number",
  COLLECTION: "collection",
  SEGMENT_VISUAL_STYLE: "segment-visual-style",
});

const SEGMENT_TYPES = Object.freeze({
  NAVBAR: "navbar",
  HERO: "hero",
  SPOTLIGHT: "spotlight",
  FEATURED: "featured",
  BRANDS: "brands",
  CATEGORIES: "categories",
  ABOUT: "about",
  HIGHLIGHTS: "highlights",
  CONTACT: "contact",
  FOOTER: "footer",
  FLOATING_WHATSAPP: "floatingWhatsapp",
});

const URL_MAX_LENGTH = 2000;
const VISUAL_LEVEL_LIMITS = Object.freeze({ min: 1, max: 3, step: 1 });
const IMAGE_POSITION_OPTIONS = Object.freeze([
  { value: "center", label: "Centro" },
  { value: "top", label: "Arriba" },
  { value: "bottom", label: "Abajo" },
  { value: "left", label: "Izquierda" },
  { value: "right", label: "Derecha" },
]);
const MEGA_MENU_STYLE_OPTIONS = Object.freeze([
  { value: "auto", label: "Automático según el contenido" },
  { value: "elegant", label: "Elegante · servicios" },
  { value: "catalog", label: "Catálogo · productos" },
  { value: "showcase", label: "Destacados · hasta 3 ítems" },
]);
const MEGA_MENU_CONTENT_OPTIONS = Object.freeze([
  { value: "auto", label: "Automático según el catálogo" },
  { value: "products", label: "Sólo productos" },
  { value: "services", label: "Sólo servicios" },
  { value: "mixed", label: "Productos y servicios" },
]);
const MEGA_MENU_DISPLAY_OPTIONS = Object.freeze([
  { value: "categories", label: "Sólo categorías" },
  { value: "items", label: "Sólo ítems" },
  { value: "both", label: "Categorías + ítems" },
]);
const MEGA_MENU_HIERARCHY_OPTIONS = Object.freeze([
  { value: "category-first", label: "Categoría → subcategoría" },
  { value: "subcategory-first", label: "Subcategoría → categoría" },
]);
const STYLE_OPTIONS = Object.freeze([
  { value: "style-1", label: "Estilo 1" },
  { value: "style-2", label: "Estilo 2" },
  { value: "style-3", label: "Estilo 3" },
]);
const HERO_CTA_ICON_OPTIONS = Object.freeze([
  { value: "explore", label: "Explorar / brújula" },
  { value: "storefront", label: "Tienda / local" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "phone", label: "Teléfono" },
  { value: "mail", label: "Email" },
  { value: "place", label: "Ubicación" },
  { value: "shopping", label: "Bolsa de compra" },
  { value: "event", label: "Calendario / reserva" },
  { value: "star", label: "Estrella" },
  { value: "verified", label: "Verificado" },
  { value: "autoawesome", label: "Destacado / brillo" },
  { value: "arrow", label: "Flecha" },
]);

const HIGHLIGHT_ICON_OPTIONS = Object.freeze([
  { value: "", label: "Sin ícono" },
  { value: "star", label: "Estrella" },
  { value: "support", label: "Atención" },
  { value: "verified", label: "Verificado" },
  { value: "speed", label: "Rapidez" },
  { value: "quality", label: "Calidad" },
  { value: "local", label: "Local" },
  { value: "security", label: "Seguridad" },
  { value: "autoawesome", label: "Elegancia / Destacado" },
  { value: "event", label: "Evento / Reserva" },
  { value: "groups", label: "Equipo / Personas" },
  { value: "photo", label: "Fotografía" },
  { value: "design", label: "Diseño / Creatividad" },
]);

const SEGMENT_PRESENTATION = Object.freeze({
  [SEGMENT_TYPES.NAVBAR]: Object.freeze({
    group: "structure",
    summary: "Identidad y accesos principales para orientarse rápido.",
    placement: "Primero, arriba",
    visualHint: "Barra horizontal con logo y enlaces",
    preview: "navigation",
  }),
  [SEGMENT_TYPES.HERO]: Object.freeze({
    group: "structure",
    summary: "Presenta el negocio con un mensaje fuerte y una acción clara.",
    placement: "Inicio de la página",
    visualHint: "Título grande, texto, imagen y botón",
    preview: "hero",
  }),
  [SEGMENT_TYPES.SPOTLIGHT]: Object.freeze({
    group: "commerce",
    summary: "Destaca una promoción, producto o servicio puntual.",
    placement: "Antes del catálogo",
    visualHint: "Bloque protagonista tipo promo",
    preview: "promo",
  }),
  [SEGMENT_TYPES.FEATURED]: Object.freeze({
    group: "commerce",
    summary: "Pone primero una selección corta de productos o servicios.",
    placement: "Zona comercial",
    visualHint: "Fila de tarjetas destacadas",
    preview: "cards",
  }),
  [SEGMENT_TYPES.BRANDS]: Object.freeze({
    group: "commerce",
    summary: "Refuerza variedad, respaldo o alianzas con marcas.",
    placement: "Zona comercial",
    visualHint: "Fila compacta de logos",
    preview: "logos",
  }),
  [SEGMENT_TYPES.CATEGORIES]: Object.freeze({
    group: "commerce",
    summary: "Da accesos rápidos a tipos de productos o servicios.",
    placement: "Antes o dentro del catálogo",
    visualHint: "Tarjetas cortas por categoría",
    preview: "categories",
  }),
  [SEGMENT_TYPES.ABOUT]: Object.freeze({
    group: "content",
    summary: "Cuenta quién sos, tu historia o la propuesta del negocio.",
    placement: "Después de la propuesta principal",
    visualHint: "Texto e imagen en dos columnas",
    preview: "split",
  }),
  [SEGMENT_TYPES.HIGHLIGHTS]: Object.freeze({
    group: "content",
    summary: "Resume beneficios o motivos concretos para elegirte.",
    placement: "Antes del contacto",
    visualHint: "Beneficios breves en tarjetas",
    preview: "benefits",
  }),
  [SEGMENT_TYPES.CONTACT]: Object.freeze({
    group: "content",
    summary: "Reúne datos y accesos para que puedan contactarte.",
    placement: "Cerca del final",
    visualHint: "Datos, acciones y ubicación",
    preview: "contact",
  }),
  [SEGMENT_TYPES.FOOTER]: Object.freeze({
    group: "closing",
    summary: "Cierra el sitio con identidad, enlaces y datos útiles.",
    placement: "Último, abajo",
    visualHint: "Franja final con varias columnas",
    preview: "footer",
  }),
  [SEGMENT_TYPES.FLOATING_WHATSAPP]: Object.freeze({
    group: "closing",
    summary: "Mantiene WhatsApp disponible durante todo el recorrido.",
    placement: "Flotante sobre la página",
    visualHint: "Botón circular fijo en una esquina",
    preview: "floating",
  }),
});

function clone(value) {
  if (value === undefined) return undefined;
  if (value === null || typeof value !== "object") return value;
  return JSON.parse(JSON.stringify(value));
}

function getAtPath(source, path, fallback) {
  const value = String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((current, rawKey) => {
      const key = /^\d+$/.test(rawKey) ? Number(rawKey) : rawKey;
      return current?.[key];
    }, source);
  return value === undefined ? fallback : value;
}

function field(path, type, options = {}) {
  const hasExplicitDefault = Object.prototype.hasOwnProperty.call(
    options,
    "defaultValue"
  );
  return {
    path,
    type,
    editable: options.editable !== false,
    defaultValue: clone(
      hasExplicitDefault ? options.defaultValue : getAtPath(emptySettings, path)
    ),
    ...options,
  };
}

function visualStyleProperty(sectionId) {
  return field("theme.template", SEGMENT_FIELD_TYPES.SEGMENT_VISUAL_STYLE, {
    key: "visualStyle",
    label: "Estilo visual del segmento",
    sectionId,
    defaultValue: clone(DEFAULT_SEGMENT_VISUAL_STYLE),
    limits: {
      titleSize: VISUAL_LEVEL_LIMITS,
      imageSize: VISUAL_LEVEL_LIMITS,
      spacing: VISUAL_LEVEL_LIMITS,
      textStyle: VISUAL_LEVEL_LIMITS,
    },
  });
}

function globalColorProperties() {
  return [
    field("theme.primaryColor", SEGMENT_FIELD_TYPES.COLOR, {
      label: "Color principal",
      maxLength: 100,
      colorScope: "site",
      shared: true,
    }),
    field("theme.secondaryColor", SEGMENT_FIELD_TYPES.COLOR, {
      label: "Color secundario",
      maxLength: 100,
      colorScope: "site",
      shared: true,
    }),
    field("theme.surfaceColor", SEGMENT_FIELD_TYPES.COLOR, {
      label: "Superficie",
      maxLength: 100,
      colorScope: "site",
      shared: true,
    }),
    field("theme.backgroundColor", SEGMENT_FIELD_TYPES.COLOR, {
      label: "Fondo general",
      maxLength: 100,
      colorScope: "site",
      shared: true,
    }),
    field("theme.textColor", SEGMENT_FIELD_TYPES.COLOR, {
      label: "Texto principal",
      maxLength: 100,
      colorScope: "site",
      shared: true,
    }),
    field("theme.mutedTextColor", SEGMENT_FIELD_TYPES.COLOR, {
      label: "Texto secundario",
      maxLength: 100,
      colorScope: "site",
      shared: true,
    }),
  ];
}

const SECTION_APPEARANCE_FALLBACKS = Object.freeze({
  about: Object.freeze({
    backgroundColor: { fallbackPath: "theme.backgroundColor" },
    surfaceColor: { fallbackValue: "transparent", overrideSeedPath: "theme.surfaceColor" },
    accentColor: { fallbackPath: "theme.primaryColor" },
    titleColor: { fallbackPath: "theme.secondaryColor" },
    textColor: { fallbackPath: "theme.mutedTextColor" },
  }),
  highlights: Object.freeze({
    backgroundColor: { fallbackPath: "theme.surfaceColor" },
    surfaceColor: { fallbackPath: "theme.surfaceColor" },
    accentColor: { fallbackPath: "theme.primaryColor" },
    titleColor: { fallbackPath: "theme.secondaryColor" },
    textColor: { fallbackPath: "theme.mutedTextColor" },
  }),
  contact: Object.freeze({
    backgroundColor: { fallbackPath: "theme.secondaryColor" },
    surfaceColor: { fallbackValue: "rgba(255,255,255,.08)", overrideSeedValue: "#FFFFFF" },
    accentColor: { fallbackPath: "theme.primaryColor" },
    titleColor: { fallbackValue: "#FFFFFF" },
    textColor: { fallbackValue: "rgba(255,255,255,.82)", overrideSeedValue: "#FFFFFF" },
  }),
});

function appearanceProperties(sectionId) {
  const root = `appearance.${sectionId}`;
  const fallbacks = SECTION_APPEARANCE_FALLBACKS[sectionId] || {};
  const optionsFor = (role) => ({
    maxLength: 100,
    colorScope: "section",
    ...(fallbacks[role] || {}),
  });

  return [
    field(`${root}.backgroundColor`, SEGMENT_FIELD_TYPES.COLOR, {
      label: "Fondo del segmento",
      ...optionsFor("backgroundColor"),
    }),
    field(`${root}.surfaceColor`, SEGMENT_FIELD_TYPES.COLOR, {
      label: "Superficie del segmento",
      ...optionsFor("surfaceColor"),
    }),
    field(`${root}.accentColor`, SEGMENT_FIELD_TYPES.COLOR, {
      label: "Color destacado",
      ...optionsFor("accentColor"),
    }),
    field(`${root}.titleColor`, SEGMENT_FIELD_TYPES.COLOR, {
      label: "Color de títulos",
      ...optionsFor("titleColor"),
    }),
    field(`${root}.textColor`, SEGMENT_FIELD_TYPES.COLOR, {
      label: "Color de textos",
      ...optionsFor("textColor"),
    }),
  ];
}

function commonSegment({
  id,
  visibilityPath,
  requiresProducts = false,
  elementEditingReady = false,
  elementTargets = null,
  defaultElementId = "",
  shellPropertyPaths = [],
  shellAdvancedGroups = [],
  visibilityCompanionPaths = [],
  visibilityLabel = "",
  shellVisualBackground = true,
  shellVisualPalette = true,
  properties,
}) {
  return {
    id,
    type: id,
    label: SEGMENT_LABELS[id] || id,
    visibilityPath,
    requiresProducts,
    elementEditingReady: elementEditingReady === true,
    elementTargets: elementTargets && typeof elementTargets === "object" ? elementTargets : {},
    defaultElementId: String(defaultElementId || "").trim(),
    shellPropertyPaths: Array.isArray(shellPropertyPaths) ? shellPropertyPaths : [],
    shellAdvancedGroups: Array.isArray(shellAdvancedGroups) ? shellAdvancedGroups : [],
    visibilityCompanionPaths: Array.isArray(visibilityCompanionPaths) ? visibilityCompanionPaths : [],
    visibilityLabel: String(visibilityLabel || "").trim(),
    shellVisualBackground: shellVisualBackground !== false,
    shellVisualPalette: shellVisualPalette !== false,
    defaultVisible: visibilityPath
      ? getAtPath(emptySettings, visibilityPath, true) !== false
      : true,
    presentation: SEGMENT_PRESENTATION[id] || null,
    properties,
  };
}


export {
  SEGMENT_FIELD_TYPES,
  SEGMENT_TYPES,
  URL_MAX_LENGTH,
  VISUAL_LEVEL_LIMITS,
  IMAGE_POSITION_OPTIONS,
  MEGA_MENU_STYLE_OPTIONS,
  MEGA_MENU_CONTENT_OPTIONS,
  MEGA_MENU_DISPLAY_OPTIONS,
  MEGA_MENU_HIERARCHY_OPTIONS,
  STYLE_OPTIONS,
  HERO_CTA_ICON_OPTIONS,
  HIGHLIGHT_ICON_OPTIONS,
  SEGMENT_PRESENTATION,
  clone,
  getAtPath,
  field,
  visualStyleProperty,
  globalColorProperties,
  SECTION_APPEARANCE_FALLBACKS,
  appearanceProperties,
  commonSegment
};
